<template>
  <div class="banderole-view flex h-[calc(100vh-80px)]">
    <!-- Détecteur de changement de marque -->
    <BrandChangeDetector :support-id="route.params.id" />

    <!-- Overlay de chargement -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
      </div>
    </div>

    <!-- Panneau latéral -->
    <ProductsPanel />

    <!-- Zone principale -->
    <div class="flex-1 bg-gray-100 p-8 overflow-auto">
      <div class="max-w-7xl mx-auto">
        <!-- En-tête -->
        <div class="mb-8 flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {{
              route.params.id
                ? "Édition d'une banderole"
                : "Création d'une banderole"
            }}
          </h1>
          <div class="flex items-center gap-4">
            <button
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="isSaving"
              @click="saveSupport"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin" />
              <span>{{ isSaving ? "Enregistrement..." : "Enregistrer" }}</span>
            </button>
            <button
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 flex items-center gap-2"
              :disabled="isSaving"
              @click="generateAndDownloadPDF"
            >
              <i class="fas fa-file-pdf" />
              Générer PDF
            </button>
          </div>
        </div>

        <!-- Canvas -->
        <div
          class="bg-white shadow rounded-lg w-[1200px] flex flex-col mx-auto"
        >
          <BanderoleCanvas
            ref="banderoleCanvasRef"
            :title="title"
            :validity-period="validityPeriod"
            :selected-product="selectedProduct"
            @update:title="title = $event"
            @update:validity-period="validityPeriod = $event"
            @update:selected-product="selectedProduct = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import productService from "@/api/catalog/product.service";
import BanderoleCanvas from "@/components/supports/banderoles/BanderoleCanvas.vue";
import BrandChangeDetector from "@/components/supports/BrandChangeDetector.vue";
import ProductsPanel from "@/components/supports/panels/ProductsPanel.vue";
import { useDocumentStore } from "@/stores/document";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import type { Product } from "@/types/product"; // Assurez-vous que ce type existe
import { onMounted, ref } from "@vue/runtime-core";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const supportStore = useSupportStore();
const documentStore = useDocumentStore();
const toast = useToast();
const route = useRoute();
const router = useRouter();

// État local
const title = ref<string>("Titre de l'offre");
const validityPeriod = ref<string>("du XX au XX mois ANNÉE");
const selectedProduct = ref<Product | null>(null);
const successSound = new Audio("/sounds/success.wav");
const errorSound = new Audio("/sounds/error.mp3");
const isSaving = ref(false);
const banderoleCanvasRef = ref();

const playSound = (sound: HTMLAudioElement) => {
  try {
    sound.currentTime = 0; // Réinitialiser le son
    sound.play().catch((error) => {
      console.warn("Erreur lors de la lecture du son:", error);
    });

    // Couper le son après 200ms pour le son iPhone
    setTimeout(() => {
      sound.pause();
      sound.currentTime = 0;
    }, 400);
  } catch (error) {
    console.warn("Erreur lors de la lecture du son:", error);
  }
};

// Ajuster le volume des sons
successSound.volume = 0.3; // 30% du volume pour le son iPhone
errorSound.volume = 0.15; // 15% du volume pour l'erreur

const saveSupport = async (): Promise<void> => {
  try {
    isSaving.value = true;

    // Récupérer les données utilisateur
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const brand = localStorage.getItem("brand") || "hv";

    // Définir le support_group_id en fonction de la marque
    let supportGroupId = 1; // HV par défaut
    if (brand === "valtra") {
      supportGroupId = 2;
    } else if (brand === "fendt") {
      supportGroupId = 3;
    }

    // Récupérer le bon support_header_id
    let supportHeaderId: number | null = null;
    try {
      const headers = await supportStore.supportHeadersService.fetchHeaders(
        brand,
        "banderole"
      );
      console.log("En-têtes récupérés pour la banderole:", headers);
      
      if (headers && headers.length > 0) {
        // Chercher d'abord un en-tête spécifique au type banderole
        const typeSpecificHeader = headers.find(
          (h: any) => h.type === "banderole"
        );
        if (typeSpecificHeader) {
          supportHeaderId = typeSpecificHeader.id;
          console.log("En-tête spécifique banderole trouvé:", supportHeaderId);
        } else {
          // Sinon, utiliser le premier en-tête disponible
          supportHeaderId = headers[0].id;
          console.log("Premier en-tête disponible utilisé:", supportHeaderId);
        }
      } else {
        console.warn("Aucun en-tête trouvé pour la marque:", brand);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des en-têtes:", error);
    }
    
    // Si aucun support_header_id valide n'a été trouvé, ne pas l'inclure dans les données
    if (!supportHeaderId) {
      console.warn("Aucun support_header_id valide trouvé, le champ ne sera pas envoyé");
    }

    // Prépare la page unique de la banderole
    const page = {
      page_number: 1,
      nb_max_products: 1,
      page_settings: {
        margin_top: 10,
        margin_bottom: 10,
        margin_left: 10,
        margin_right: 10,
        bleed: 5,
        safe_area: true,
      },
      background_image: null,
      page_zoom: 1.0,
      page_rotation: 0,
      page_position: {
        x: 0,
        y: 0,
        width: 595,
        height: 842,
      },
      elements: selectedProduct.value
        ? [
            {
              id: selectedProduct.value.id,
              type: "product",
              product_id: selectedProduct.value.id,
              name: selectedProduct.value.name || "",
              description: selectedProduct.value.description || "",
              image: selectedProduct.value.image,
              product_image_id:
                selectedProduct.value.product_image_id ||
                selectedProduct.value.id,
              product_image: selectedProduct.value.image,
              price_ht: selectedProduct.value.price
                ? parseFloat(selectedProduct.value.price.toString())
                : null,
              price_ttc: selectedProduct.value.price
                ? parseFloat(selectedProduct.value.price.toString())
                : null,
              price_old: null,
              discount_mode: "none",
              pictogram_id: null,
              brand_id: null, // Pour les banderoles, on n'utilise pas de brand_id spécifique
              custom_styles: [],
              hook: selectedProduct.value.name || "",
              element_settings: {
                element_type: "product",
                element_id: selectedProduct.value.id,
                product_id: selectedProduct.value.id,
              },
            },
          ]
        : [],
    };

    const supportData: any = {
      name: title.value,
      dates: validityPeriod.value,
      type: "banderole",
      price_type: 1,
      support_template_id: 5, // ID spécifique pour les banderoles
      published: false,
      agco: userData.roles && userData.roles.includes("AGCO"),
      owner_id: userData.id || 1,
      nb_pages: 1,
      support_group_id: supportGroupId,
      highlight_bg: false,
      format: "A4",
      highlight_bg_color: "#FFFFFF",
      display_offre: "none",
      date_publish: null,
      zoom_level: "1.00",
      background_color: "#FFFFFF",
      font_family: "Arial",
      font_size: 12,
      layout_settings: {
        gridEnabled: true,
        gridSize: 10,
        snapToGrid: true,
        showGuides: true,
        showRulers: true,
      },
      custom_styles: [],
      products: [],
      html: "",
      pages: [page],
    };
    
    // N'ajouter support_header_id que s'il est valide
    if (supportHeaderId) {
      supportData.support_header_id = supportHeaderId;
    }

    // Si on est en mode édition, ajouter l'ID
    if (route.params.id) {
      supportData.id = parseInt(route.params.id as string);
    }

    const response = await supportStore.saveSupport(supportData);
    if (response?.data?.id) {
      supportStore.currentSupport.id = response.data.id;
    }

    // FormData (sans PDF : le serveur génère le vectoriel ; capture en fallback)
    const formData = new FormData();
    formData.append("supportId", supportStore.currentSupport.id.toString());
    formData.append("brandSlug", localStorage.getItem("brand") || "hv");
    formData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );

    // Persistance du PDF (vectoriel async + notification ; capture en fallback)
    const result = await supportStore.persistSupportPdf(
      formData,
      () => banderoleCanvasRef.value.generatePdfBlob(),
      // Banderole : capture client (screenshot) — meilleur rendu que le vectoriel.
      undefined,
      true
    );

    // En fallback (capture), la réponse porte pdf_name ; en async il arrive via le job.
    const pdfName = (result as any)?.data?.pdf_name;
    if (pdfName && supportStore.currentSupport) {
      supportStore.currentSupport.pdf_name = pdfName;
    }

    playSound(successSound);
    toast.success("Support sauvegardé avec succès");

    // Rafraîchir la liste des documents pour inclure le nouveau pdf_name
    await documentStore.fetchDocuments();

    // Petit délai pour s'assurer que les données sont bien mises à jour
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Redirection vers /documents après sauvegarde
    router.push("/documents");
  } catch (error) {
    console.error("Erreur lors de la sauvegarde de la banderole:", error);
    playSound(errorSound);
    toast.error("Erreur lors de la sauvegarde de la banderole");
  } finally {
    isSaving.value = false;
  }
};

const generateAndDownloadPDF = async () => {
  try {
    isSaving.value = true;
    const pdfBlob = await banderoleCanvasRef.value.generatePdfBlob();
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.value || "banderole"}-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error("Erreur lors de la génération du PDF :", error);
    toast.error("Erreur lors de la génération du PDF");
  } finally {
    isSaving.value = false;
  }
};

onMounted(async (): Promise<void> => {
  const supportId = route.params.id;
  if (supportId) {
    // Mode édition
    try {
      await supportStore.loadSupport("banderole", supportId as string);
      if (supportStore.currentSupport) {
        title.value = supportStore.currentSupport.name;
        validityPeriod.value = supportStore.currentSupport.dates;
        const page = supportStore.currentSupport.pages?.[0];
        const element = page?.elements?.[0];

        if (element && element.product_id) {
          // Récupérer les données complètes du produit
          try {
            const productData = await productService.getProductById(
              element.product_id
            );
            selectedProduct.value = productData || element;
          } catch (error) {
            console.error("Erreur lors de la récupération du produit:", error);
            // En cas d'erreur, utiliser l'élément de la page
            selectedProduct.value = element;
          }
        } else {
          selectedProduct.value = element || null;
        }
      }
    } catch (error) {
      toast.error("Erreur lors du chargement de la banderole");
    }
  } else {
    // Mode création
    supportStore.initSupport("banderole");
  }
});
</script>

<script lang="ts">
export default {
  name: "BanderoleView",
};
</script>

<style scoped>
/* Styles pour l'impression et l'aperçu */
@media print {
  .support-preview {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
